import { get, head, filter, partition } from 'lodash';

import {
	FETCH_MY_PAYMENTS_TO_AUTHORISE_SUCCESS,
	FETCH_OTHER_USER_PAYMENTS_TO_AUTHORISE_SUCCESS,
	FETCH_ALL_ACCOUNT_PAYMENTS_TO_AUTHORISE_SUCCESS,
} from '../page/actions';
import {
	SELECT_AWAITING_AUTH_TAB,
	CHANGE_MY_PAYMENTS_SORT,
	SHOW_MY_BATCH,
	HIDE_MY_BATCH,
	SHOW_OTHER_USER_BATCH,
	HIDE_OTHER_USER_BATCH,
	SELECT_TO_AUTHORISE,
	SELECT_TO_VIEW,
	HIDE_EXPIRED_PAYMENTS,
	SELECT_TO_EDIT,
	CHANGE_OTHER_USER_PAYMENTS_SORT,
} from './actions';
import { BACS_CREDIT_CODE, TYPE_IFT, TYPE_CHAPS, TYPE_FASTER_PAYMENTS } from '../../config';
import { PENDING, REVIEWED } from '../../constants/Constants';

const defaultState = {
	activeAwaitingAuthTab: 'you',
	myPaymentsSortAscending: true,
	otherUserPaymentsSortAscending: true,
};

const filterPendingApprovers = (approvers = []) => filter(approvers, { status: PENDING || REVIEWED });

export const mapSingleFPtoPayments = (payments) =>
	payments.map((payment) => ({
		...payment,
		type: TYPE_FASTER_PAYMENTS,
		approvers: filterPendingApprovers(payment.approvers),
	}));

export const mapChapsToPayments = (chapsApprovals) =>
	chapsApprovals.map((approval) => ({
		...approval,
		type: TYPE_CHAPS,
		start_date: approval.submission_date,
		approvers: approval.approvers,
	}));

export const rollUpBatchPayments = (allPayments, paymentType = TYPE_FASTER_PAYMENTS) => {
	const [singlePayments, batchPayments] = partition(allPayments, (payment) => get(payment, 'batch_id', '') === '');

	const batchesGrouped = batchPayments.reduce(
		(result, payment) => ({
			...result,
			[payment.batch_id]: [
				...(result[payment.batch_id] || []),
				{
					...payment,
					approvers: filterPendingApprovers(payment.approvers),
				},
			],
		}),
		[]
	);

	const getAmount = (batch) => ({
		currency: 'GBP',
		value: batch.reduce((result, payment) => result + payment.amount.value, 0),
	});

	const getApprovers = (firstPaymentInBatch) =>
		firstPaymentInBatch.approvers.map((item) => ({
			status: item.status,
			approver: item.approver,
		}));

	const batchPaymentSummaryList = Object.keys(batchesGrouped).map((key) => {
		const firstPaymentInBatch = head(batchesGrouped[key]);
		const payment = {
			id: key,
			isBatch: true,
			type: paymentType,
			amount: getAmount(batchesGrouped[key]),
			batch: batchesGrouped[key],
			status: firstPaymentInBatch.status,
			to_account: { name: firstPaymentInBatch.batch_name },
			from_account: firstPaymentInBatch.from_account,
			reference: ' - ',
			start_date: firstPaymentInBatch.submission_date || firstPaymentInBatch.start_date,
			creator_id: firstPaymentInBatch.creator_id,
			batch_type: firstPaymentInBatch.batch_type,
			approvers: getApprovers(firstPaymentInBatch),
		};
		return payment;
	});

	let singlePaymentsWithApprovers;

	if (paymentType === TYPE_CHAPS) {
		singlePaymentsWithApprovers = mapChapsToPayments(singlePayments);
	} else {
		singlePaymentsWithApprovers = mapSingleFPtoPayments(singlePayments);
	}

	return [...singlePaymentsWithApprovers, ...batchPaymentSummaryList];
};

export const mapBacsToPayments = (bacsApprovals) => {
	const payments = bacsApprovals.map((approval) => ({
		...approval,
		bacsType: approval.type,
		type: 'bacs',
		start_date: approval.submission_date,
		entries: approval.entries.map((entry) => ({
			...entry,
			status: 'Pending Approval',
		})),
		approvers: filterPendingApprovers(approval.approvers),
	}));
	return payments;
};

export const mapIFTsToPayments = (approvals) =>
	approvals.map((approval) => ({
		...approval,
		type: TYPE_IFT,
		start_date: approval.submission_date,
		approvers: approval.approvers,
	}));

const getPaymentsToAuthFromApprovals = (action) => {
	const approvals = get(action, 'payload.approvals', []);
	const fasterPayments = rollUpBatchPayments(approvals);

	const bacsApprovals = get(action, 'payload.approvals_bacs', []);
	const bacsPayments = mapBacsToPayments(bacsApprovals);

	const chapsApprovals = get(action, 'payload.approvals_chaps', []);
	const chapsPayments = rollUpBatchPayments(chapsApprovals, TYPE_CHAPS);

	const iftsApprovals = get(action, 'payload.approvals_ift', []);
	const itfPayments = mapIFTsToPayments(iftsApprovals);

	const allPaymentsToAuth = [...fasterPayments, ...bacsPayments, ...chapsPayments, ...itfPayments];

	return allPaymentsToAuth;
};

export const mapEntryToPayment = (entry, bacsDetails) => {
	const {
		creator_id,
		creator_name,
		bacs_name: batch_name,
		settlement_date,
		type: bacsType,
		submission_date,
		bacs_id,
	} = bacsDetails;

	const { reference, id, amount, hmrc_reference, transaction_code } = entry;

	const from_account = bacsDetails.type === BACS_CREDIT_CODE ? bacsDetails.account : entry.account;
	const to_account = bacsDetails.type === BACS_CREDIT_CODE ? entry.account : bacsDetails.account;
	const beneficiary_name = get(entry, 'account.name', '');

	return {
		isBacs: true,
		amount,
		from_account,
		to_account,
		reference,
		start_date: submission_date,
		batch_name,
		creator_id,
		id,
		beneficiary_name,
		settlement_date,
		submission_date,
		bacsType,
		bacs_id,
		hmrc_reference,
		transaction_code,
		creator_name,
	};
};

export const mapBacsVerificationToPayments = (verification) => {
	const bacsDetails = verification.bacs;
	const payments = verification.bacs.entries.map((entry) => mapEntryToPayment(entry, bacsDetails));
	const bacsType = get(head(payments), 'bacsType', '');
	return { ...verification, payments, type: 'bacs', bacsType };
};

export const mapChapsVerificationToPayments = (verification) => {
	const payments = verification.chaps.map(({ payment_uuid, submission_date, ...otherChapsDetails }) => ({
		...otherChapsDetails,
		id: payment_uuid,
		start_date: submission_date,
		submission_date,
		type: TYPE_CHAPS,
	}));
	return { ...verification, payments, type: TYPE_CHAPS };
};
export const mapIFTVerificationToPayments = (verification) => {
	const payments = [
		{
			...verification.ift,
			id: get(verification, 'ift.payment_uuid'),
			start_date: get(verification, 'ift.submission_date'),
			type: TYPE_IFT,
		},
	];
	return { ...verification, payments, type: TYPE_IFT };
};

const payments = (state = defaultState, action) => {
	if (action.type === FETCH_MY_PAYMENTS_TO_AUTHORISE_SUCCESS) {
		const myPaymentsToAuthorise = get(action, 'payload.verification_list', []).map((toAuth) => {
			if (toAuth.bacs) return mapBacsVerificationToPayments(toAuth);
			if (toAuth.chaps) return mapChapsVerificationToPayments(toAuth);
			if (toAuth.ift) return mapIFTVerificationToPayments(toAuth);
			const payments = get(toAuth, 'payments', []);
			return {
				...toAuth,
				payments: payments.map((payment) => ({
					...payment,
					id: payment.payment_uuid,
				})),
			};
		});

		const hasExpiredPayments = myPaymentsToAuthorise.some((payment) => payment.status === 'CUTOFF');

		return {
			...state,
			myPaymentsToAuthorise,
			showExpired: hasExpiredPayments,
		};
	}

	if (action.type === FETCH_OTHER_USER_PAYMENTS_TO_AUTHORISE_SUCCESS) {
		const otherUserPaymentsToAuthorise = getPaymentsToAuthFromApprovals(action);
		return { ...state, otherUserPaymentsToAuthorise };
	}

	if (action.type === FETCH_ALL_ACCOUNT_PAYMENTS_TO_AUTHORISE_SUCCESS) {
		const allAccountPaymentsToAuthorise = getPaymentsToAuthFromApprovals(action);
		return { ...state, allAccountPaymentsToAuthorise };
	}

	if (action.type === SELECT_AWAITING_AUTH_TAB) {
		return {
			...state,
			activeAwaitingAuthTab: action.activeAwaitingAuthTab,
		};
	}

	if (action.type === SHOW_MY_BATCH) {
		const paymentIndex = state.myPaymentsToAuthorise.findIndex((payment) => payment.verification_id === action.batchId);
		if (paymentIndex !== -1) {
			const newState = state;
			newState.myPaymentsToAuthorise[paymentIndex].showBatch = true;
			return newState;
		}
		return state;
	}

	if (action.type === HIDE_MY_BATCH) {
		const paymentIndex = state.myPaymentsToAuthorise.findIndex((payment) => payment.verification_id === action.batchId);
		if (paymentIndex !== -1) {
			const newState = state;
			newState.myPaymentsToAuthorise[paymentIndex].showBatch = false;
			return newState;
		}
		return state;
	}

	if (action.type === SHOW_OTHER_USER_BATCH) {
		if (state.otherUserPaymentsToAuthorise) {
			const paymentIndex = state.otherUserPaymentsToAuthorise.findIndex((payment) => payment.id === action.batchId);
			if (paymentIndex !== -1) {
				const newState = state;
				newState.otherUserPaymentsToAuthorise[paymentIndex].showBatch = true;
				return newState;
			}
		}

		if (state.allAccountPaymentsToAuthorise) {
			const paymentIndex = state.allAccountPaymentsToAuthorise.findIndex((payment) => payment.id === action.batchId);
			if (paymentIndex !== -1) {
				const newState = state;
				newState.allAccountPaymentsToAuthorise[paymentIndex].showBatch = true;
				return newState;
			}
		}

		return state;
	}

	if (action.type === HIDE_OTHER_USER_BATCH) {
		if (state.otherUserPaymentsToAuthorise) {
			const paymentIndex = state.otherUserPaymentsToAuthorise.findIndex((payment) => payment.id === action.batchId);
			if (paymentIndex !== -1) {
				const newState = state;
				newState.otherUserPaymentsToAuthorise[paymentIndex].showBatch = false;
				return newState;
			}
		}

		if (state.allAccountPaymentsToAuthorise) {
			const paymentIndex = state.allAccountPaymentsToAuthorise.findIndex((payment) => payment.id === action.batchId);
			if (paymentIndex !== -1) {
				const newState = state;
				newState.allAccountPaymentsToAuthorise[paymentIndex].showBatch = false;
				return newState;
			}
		}

		return state;
	}

	if (action.type === CHANGE_MY_PAYMENTS_SORT) {
		return { ...state, myPaymentsSortColumn: action.myPaymentsSortColumn };
	}

	if (action.type === CHANGE_OTHER_USER_PAYMENTS_SORT) {
		return {
			...state,
			otherUserPaymentsSortColumn: action.otherUserPaymentsSortColumn,
		};
	}

	if (action.type === SELECT_TO_AUTHORISE) {
		return { ...state, toAuthorise: action.toAuthorise };
	}

	if (action.type === SELECT_TO_VIEW) {
		return { ...state, toView: action.toView };
	}
	if (action.type === SELECT_TO_EDIT) {
		return { ...state, toEdit: action.toEdit };
	}
	if (action.type === HIDE_EXPIRED_PAYMENTS) {
		return { ...state, showExpired: false };
	}

	return state;
};

export default payments;
